import pencilIcon from "@tabler/icons/outline/pencil.svg";
import React, { useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import VerificationBadge from "@/components/verification-badge";
import { useAuth } from "@/contexts/auth/useAuth";
import ActionButton from "@/features/ui/components/action-button";

import AccountPopover from "./popovers/account-popover";
import PremiumBadge from "./premium-badge";
import RelativeTimestamp from "./relative-timestamp";
import { Avatar, Badge, HStack, Icon, IconButton, Stack, Text } from "./ui";

import type { Account as AccountSchema } from "@/schemas";

const messages = defineMessages({
  bot: { id: "account.badges.bot", defaultMessage: "Bot" },
});

export interface AccountProps {
  account: AccountSchema;
  action?: React.ReactElement;
  actionAlignment?: "center" | "top";
  actionIcon?: string;
  actionTitle?: string;
  /** Override other actions for specificity like mute/unmute.  */
  actionType?: "muting" | "blocking";
  avatarSize?: number;
  editedAt?: string | null;
  hidden?: boolean;
  hideActions?: boolean;
  id?: string;
  onActionClick?: (account: any) => void;
  showProfileHoverCard?: boolean;
  timestamp?: string;
  timestampUrl?: string;
  futureTimestamp?: boolean;
  withAccountNote?: boolean;
  withLinkToProfile?: boolean;
  withRelationship?: boolean;
  style?: React.CSSProperties;
  note?: string;
}

const Account = ({
  account,
  actionType,
  action,
  actionIcon,
  actionTitle,
  actionAlignment = "center",
  avatarSize = 42,
  hidden = false,
  editedAt,
  hideActions = false,
  onActionClick,
  showProfileHoverCard = true,
  style = {},
  timestamp,
  timestampUrl,
  futureTimestamp = false,
  withAccountNote = false,
  withLinkToProfile = true,
  withRelationship = true,
  note,
}: AccountProps) => {
  const overflowRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLDivElement>(null);
  const { currentAccount } = useAuth();

  const handleAction = () => {
    onActionClick!(account);
  };

  const renderAction = () => {
    if (action) {
      return action;
    }

    if (hideActions) {
      return null;
    }

    if (onActionClick && actionIcon) {
      return (
        <IconButton
          src={actionIcon}
          title={actionTitle}
          onClick={handleAction}
          iconClassName="h-4 w-4"
        />
      );
    }

    if (account.id !== currentAccount!.id) {
      return (
        <ActionButton
          account={account}
          actionType={actionType}
          buttonSize="sm"
        />
      );
    }

    return null;
  };

  const intl = useIntl();

  if (!account) {
    return null;
  }

  if (hidden) {
    return (
      <>
        {account.display_name}
        {account.acct}
      </>
    );
  }

  const LinkEl: any = withLinkToProfile ? Link : "div";
  const linkProps = withLinkToProfile
    ? {
        to: `/@${account.acct}`,
        title: account.acct,
      }
    : {};

  return (
    <div
      data-testid="account"
      className="group min-w-0"
      style={style}
      ref={overflowRef}
    >
      <HStack alignItems={actionAlignment} justifyContent="between" space={3}>
        <HStack
          alignItems={withAccountNote || note ? "top" : "center"}
          space={3}
          className="overflow-hidden"
        >
          <AccountPopover isEnabled={showProfileHoverCard} account={account}>
            <LinkEl {...linkProps}>
              <Avatar src={account.avatar_static} size={avatarSize} />
            </LinkEl>
          </AccountPopover>

          <div className="grow overflow-hidden">
            <AccountPopover isEnabled={showProfileHoverCard} account={account}>
              <LinkEl {...linkProps}>
                <HStack space={1} alignItems="center" grow>
                  <Text
                    size="sm"
                    weight="semibold"
                    truncate
                    dangerouslySetInnerHTML={{
                      __html: account.display_name_html,
                    }}
                  />

                  {account.verified && <VerificationBadge />}
                  {account.premium && <PremiumBadge />}

                  {account.bot && (
                    <Badge variant="secondary">
                      {intl.formatMessage(messages.bot)}
                    </Badge>
                  )}
                </HStack>
              </LinkEl>
            </AccountPopover>

            <Stack space={withAccountNote || note ? 1 : 0}>
              <HStack alignItems="center" space={1}>
                <Text theme="muted" size="sm" direction="ltr" truncate>
                  @{account.acct}
                </Text>

                {timestamp ? (
                  <>
                    <Text tag="span" theme="muted" size="sm">
                      &middot;
                    </Text>

                    {timestampUrl ? (
                      <Link
                        to={timestampUrl}
                        className="hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <RelativeTimestamp
                          timestamp={timestamp}
                          theme="muted"
                          size="sm"
                          className="whitespace-nowrap"
                          futureDate={futureTimestamp}
                        />
                      </Link>
                    ) : (
                      <RelativeTimestamp
                        timestamp={timestamp}
                        theme="muted"
                        size="sm"
                        className="whitespace-nowrap"
                        futureDate={futureTimestamp}
                      />
                    )}
                  </>
                ) : null}

                {currentAccount?.features?.edits_enabled && editedAt && (
                  <Icon
                    className="text-muted-foreground"
                    size={16}
                    src={pencilIcon}
                  />
                )}

                {actionType === "muting" && account.mute_expires_at ? (
                  <>
                    <Text tag="span" theme="muted" size="sm">
                      &middot;
                    </Text>

                    <Text theme="muted" size="sm">
                      <RelativeTimestamp
                        timestamp={account.mute_expires_at}
                        futureDate
                      />
                    </Text>
                  </>
                ) : null}
              </HStack>

              {note ? (
                <Text size="sm" className="mr-2">
                  {note}
                </Text>
              ) : (
                withAccountNote && (
                  <Text
                    size="sm"
                    dangerouslySetInnerHTML={{ __html: account.note_emojified }}
                    className="mr-2 rtl:mr-0 rtl:ml-2"
                  />
                )
              )}
            </Stack>
          </div>
        </HStack>

        <div ref={actionRef}>{withRelationship ? renderAction() : null}</div>
      </HStack>
    </div>
  );
};

export default Account;
